/**
 * Hero Franchise Section Component
 * Updated for Content API format
 */

"use client";

import {JSX} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {Hero} from "@/sections/content/hero";

import {useTranslation} from "@/shared/i18n/use-translation";
import {ButtonBase} from "@/shared/ui/buttons";

import {mapHeroFranchiseData} from "./mapper";
import type {HeroFranchiseSectionData} from "./type";

interface HeroFranchiseSectionProps {
    data: HeroFranchiseSectionData;
}

export function HeroFranchiseSection({data}: HeroFranchiseSectionProps): JSX.Element | null {
    const {t} = useTranslation();

    const mappedData = mapHeroFranchiseData(data);

    if (!mappedData) {
        return null;
    }

    const {imageUrl, imageAlt, titleHtml, heroText, badgeIconUrl, badgeIconAlt, sliderImages, link} = mappedData;

    const hasSliderImages = sliderImages.length > 0;

    return (
        <Hero image={{url: imageUrl, alt: imageAlt}} type="franchise" anchorId={mappedData.anchorId}>
            <Hero.Title size={{desktop: 32, mobile: 20}}>{titleHtml}</Hero.Title>
            {heroText && <Hero.Description size={{desktop: 16, mobile: 14}}>{heroText}</Hero.Description>}
            {link && (
                <ButtonBase as="link" className="hero__button" href={link.url} width="auto" variant="primary" size="lg" target={link.target}>
                    {link.text}
                </ButtonBase>
            )}
            {hasSliderImages && <Hero.Slider images={sliderImages} label={t("hero.asSeenIn")} />}
            {badgeIconUrl && <SmartImage src={badgeIconUrl} alt={badgeIconAlt || ""} width={150} height={150} className="hero__logo" />}
        </Hero>
    );
}
